import { FlexLayoutModule } from '@angular/flex-layout';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import {ErrorHandler, LOCALE_ID, ModuleWithProviders, NgModule, Provider} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialDesignModule } from './modules/material-design/material-design.module';
import { TextFieldComponent } from './components/fields/text-field/text-field.component';
import { TextMaskModule } from 'angular2-text-mask';
import { TextTransformInputDirective } from './directives/text-transform.directive';
import { TranslatePipe } from './pipes/translate.pipe';
import { MaterialSelectComponent } from './components/fields/select/material-select.component';
import { CheckboxComponent } from './components/fields/checkbox/checkbox.component';
import { MaterialMultiselectComponent } from './components/fields/multiselect/material-multiselect.component';
import { SlideToggleComponent } from './components/fields/slide-toggle/slide-toggle.component';
import { SectionComponent } from './components/section/section.component';
import { DatefieldService } from './components/esp-datefield/services/datefield-service';
import { EspDateFieldComponent } from './components/esp-datefield/components/datefield-component.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { InsurerDataSectionComponent } from './sections/web/insurer-data-section/insurer-data-section.component';
import { AgreementsSectionComponent } from './sections/web/agreements-section/agreements-section.component';
import { AutocompleteComponent } from './components/fields/autocomplete/autocomplete.component';
import { LanguageService } from './services/language.service';
import { SwiperModule } from 'ngx-swiper-wrapper';
import { DateFieldJQueryComponent } from './components/fields/date-field-jquery/date-field.component';
import { DecimalPlacesAccessorDirective } from './directives/decimal-places.directive';
import { TrimValueAccessorDirective } from './directives/trim-value-accessor.directive';
import { RestApiService } from './services/rest-api.service';
import { CommonApiErrorHandlerService } from './services/common-api-error-handler.service';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { NgxMaskModule } from 'ngx-mask';
import { AmountFormatterPipe } from './pipes/amount-formatter.pipe';
import { NotificationComponent } from './components/notification-wrapper/notification/notification.component';
import { RadioButtonComponent } from './components/fields/radio/radio-button.component';
import { MainPricePipe } from './pipes/main-price.pipe';
import { TailsPricePipe } from './pipes/tails-price.pipe';
import { SelectedOfferSectionComponent } from './sections/web/selected-offer-section/selected-offer-section.component';
import { PositiveNotificationWebComponent } from './sections/notifications/positive-notification/positive-notification-web.component';
import { HttpInterceptor } from './services/http-interceptor';
import { ErrorNotifySectionWebComponent } from './sections/web/error-notify-section/error-notify-section-web.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ChipsContainerComponent } from './components/chips-container/chips-container.component';
import { MultiselectAndChipListComponent } from './components/fields/multiselect-and-chip-list/multiselect-and-chip-list.component';
import { SelectComponent } from './components/fields/ng-multiselect/select.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { PhoneFieldComponent } from './components/fields/telephone-number-field/telephone-number-field.component';
import { MethodPaymentsSectionComponent } from './sections/web/method-payments-section/method-payments-section.component';
import { OfferSectionComponent } from './sections/web/offer-section/offer-section.component';
import { FxIfPipe } from './pipes/fx-if.pipe';
import { VehicleDataSectionComponent } from './sections/web/vehicle-data-section/vehicle-data-section.component';
import { OwnerDataSectionComponent } from './sections/web/owner-data-section/owner-data-section.component';
import { CoOwnerComponent } from './sections/web/owner-data-section/co-owner/co-owner.component';
import { InformationSectionComponent } from './sections/web/information-section/information-section.component';
import { VehicleModelSectionComponent } from './sections/web/vehicle-model-section/vehicle-model-section.component';
import { VehicleModelStateService } from '../core/services/impl/section-services/vehicle-model/vehicle-model-state.service';
import { OffersPresentationComponent } from './sections/web/offer-section/offers-presentation/offers-presentation.component';
import { AdditionalInformationSectionComponent } from './sections/web/additional-information-section/additional-information-section.component';
import { VehicleService } from '../core/services/impl/section-services/vehicle-model/vehicle.service';
import { SelectLabelPipe } from './pipes/select-label.pipe';
import { RequestStorageService } from './services/request-storage.service';
import { OfferPresentationCoverageItemComponent } from './sections/web/offer-section/offers-presentation/offer-presentation-coverage-item/offer-presentation-coverage-item.component';
import { VehicleDataStateService } from '../core/services/impl/section-services/vehicle-data/vehicle-data-state.service';
import { TooltipIconComponent } from './components/tooltip-icon/tooltip-icon.component';
import { TextareaComponent } from './components/fields/textarea/textarea.component';
import { ShortenedVehicleDataComponent } from './sections/web/offer-section/shortened-vehicle-data/shortened-vehicle-data.component';
import { WaitingOfferDialogComponent } from './components/dialogs/waiting-offer-dialog/waiting-offer-dialog.component';
import { OfferPrimaryBoxComponent } from './sections/web/offer-section/boxes/offer-primary-box/offer-primary-box.component';
import { OfferDetailBoxComponent } from './sections/web/offer-section/boxes/offer-detail-box/offer-detail-box.component';
import { OfferBasicBoxComponent } from './sections/web/offer-section/boxes/offer-basic-box/offer-basic-box.component';
import { OfferPresentationGroupBoxComponent } from './sections/web/offer-section/offers-presentation/offer-presentation-group-box/offer-presentation-group-box.component';
import { OfferCompareBoxComponent } from './sections/web/offer-section/boxes/offer-compare-box/offer-compare-box.component';
import { OfferMainPricePipe } from './pipes/offer-main-price.pipe';
import { OfferTailsPricePipe } from './pipes/offer-tails-price.pipe';
import { OfferFixedPriceComponent } from './sections/web/offer-section/offer-fixed-price/offer-fixed-price.component';
import { InstallmentSectionComponent } from './sections/web/installment-section/installment-section.component';
import { OfferCarValuationComponent } from './sections/web/offer-section/boxes/offer-car-valuation/offer-car-valuation.component';
import { OfferSelectedItemComponent } from './components/offer-selected-item/offer-selected-item.component';
import { OfferBasicItemComponent } from './components/offer-basic-item/offer-basic-item.component';
import { AddressDataComponent } from './sections/web/insurer-data-section/address-data/address-data.component';
import { SendOfferComponent } from './sections/web/offer-section/send-offer/send-offer.component';
import { RadioWithMessageComponent } from './components/fields/radio-with-message/radio-with-message.component';
import { InformationDialogComponent } from './components/dialogs/information-dialog/information-dialog.component';
import { AmountNounTranslationPipe } from './pipes/amount-noun-translation.pipe';
import { CollectedCarsComponent } from './sections/web/vehicle-model-section/collected-cars/collected-cars.component';
import { SendOfferNumberComponent } from './sections/web/offer-section/send-offer/send-offer-number/send-offer-number.component';
import { SelectDialogComponent } from './components/dialogs/select-dialog/select-dialog.component';
import { VehicleVersionComponent } from './components/fields/ng-multiselect/vehicle-version/vehicle-version.component';
import { SeparatorPipe } from './pipes/separator.pipe';
import { InformationIconComponent } from './components/information-icon/information-icon.component';
import { LoadingOffersDialogComponent } from './components/dialogs/loading-offer-dialog/loading-offers-dialog.component';
import { AdditionalInfoPipe } from './pipes/additional-info.pipe';
import { OfferRecalculationDialogComponent } from './components/dialogs/offer-recalculation-dialog/offer-recalculation-dialog.component';
import { CustomPriceComponent } from './components/dialogs/offer-recalculation-dialog/custom-price/custom-price.component';
import { PaymentAcceptNotificationComponent } from './sections/notifications/payment-accept-notification/payment-accept-notification.component';
import { InsurerDataAuthorizationComponent } from './sections/notifications/insurer-data-authorization-notification/insurer-data-authorization.component';
import { CessionSectionComponent } from './sections/web/cession-section/cession-section.component';
import { InsurerFailedDataAuthorizationComponent } from './sections/notifications/insurer-failed-data-authorization-notification/insurer-failed-data-authorization.component';
import { AztecScannerDialogComponent } from './components/dialogs/aztec-scanner-dialog/aztec-scanner-dialog.component';
import { AdditionalContentSectionComponent } from './sections/web/additional-content-section/additional-content-section.component';
import { AztecSectionComponent } from './sections/mobile/aztec-section/aztec-section.component';
import { SectionDataPresentationComponent } from './components/section-data-presentation/section-data-presentation.component';
import { StepInfoComponent } from './components/step-info/step-info.component';
import { AdditionalDriverComponent } from './sections/web/owner-data-section/additional-driver/additional-driver.component';
import { VehicleUseSectionComponent } from './sections/web/vehicle-use-section/vehicle-use-section.component';
import { ContactDataSectionComponent } from './sections/web/contact-data-section/contact-data-section.component';
import { OffersFiltersComponent } from './sections/web/offer-section/offers-filters/offers-filters.component';
import { OffersFilterComponent } from './sections/web/offer-section/offers-filters/offers-filter/offers-filter.component';
import { NoFilteredOffersComponent } from './sections/web/offer-section/no-filtered-offers/no-filtered-offers.component';
import { MobileFiltersDialogComponent } from './sections/web/offer-section/mobile-filters-dialog/mobile-filters-dialog.component';
import { InsuranceCoveragesSectionComponent } from './sections/web/insurance-coverages-section/insurance-coverages-section.component';
import { HintComponent } from './components/hint/hint.component';
import { MissingDataOfferComponent } from './sections/web/offer-section/missing-data-offer/missing-data-offer.component';
import { SalesMessagesSectionComponent } from './sections/web/sales-messages-section/sales-messages-section.component';
import { SalesMessageComponent } from './sections/web/sales-messages-section/sales-message/sales-message.component';
import { ShortenedVehicleInfoComponent } from './sections/web/offer-section/offers-presentation/shortened-vehicle-info/shortened-vehicle-info.component';
import { SendCalculationComponent } from './components/send-calculation/send-calculation.component';
import { InformationClauseSectionComponent } from './sections/web/information-clause-section/information-clause-section.component';
import { DiscountCodesSectionComponent } from './sections/web/discount-codes-section/discount-codes-section.component';
import { CustomerDamageDataComponent } from './sections/web/offer-section/customer-damage-data/customer-damage-data.component';
import { AcVariantsSectionComponent } from './sections/web/ac-variants-section/ac-variants-section.component';
import { AdditionalOptionsSectionComponent } from './sections/web/additional-options-section/additional-options-section.component';
import { RadioTileComponent } from './components/fields/radio-tile/radio-tile.component';
import { UniqaDiscountCodeSectionComponent } from './sections/web/uniqa-discount-code-section/uniqa-discount-code-section.component';
import { OfferModificationPriceComponent } from './sections/web/offer-section/offer-modification-price/offer-modification-price.component';
import { PzuBannerComponent } from './sections/web/offer-section/pzu-banner/pzu-banner.component';
import { OfferModificationDialogComponent } from './components/dialogs/offer-modification-dialog/offer-modification-dialog.component';
import { AssistanceCoveragesComparisonComponent } from './sections/web/additional-options-section/assistance-coverages-comparison/assistance-coverages-comparison.component';
import { AssistanceCoverageValueComponent } from './sections/web/additional-options-section/assistance-coverages-comparison/assistance-coverage-value/assistance-coverage-value.component';
import { ModifiedOfferPriceChangeDialogComponent } from './components/dialogs/modified-offer-price-change-dialog/modified-offer-price-change-dialog.component';
import { MpayAztecScannerDialogComponent } from './components/dialogs/mpay-aztec-scanner-dialog/mpay-aztec-scanner-dialog.component';
import { PersonalDataAdministratorSectionComponent } from './sections/web/personal-data-administrator-section/personal-data-administrator-section.component';
import { PowerOfAttorneySectionComponent } from './sections/web/power-of-attorney-section/power-of-attorney-section.component';
import { SendCalculationDialogComponent } from './components/dialogs/send-calculation-dialog/send-calculation-dialog.component';
import { AcVariantsBeesafeSectionComponent } from './sections/web/ac-variants-beesafe-section/ac-variants-beesafe-section.component';
import { AdditionalOptionsBeesafeSectionComponent } from './sections/web/additional-options-beesafe-section/additional-options-beesafe-section.component';
import { AcVariantsCoveragesTableComponent } from './sections/web/ac-variants-beesafe-section/ac-variants-coverages-table/ac-variants-coverages-table.component';
import { AcVariantsCoveragesRowValueComponent } from './sections/web/ac-variants-beesafe-section/ac-variants-coverages-table/ac-variants-coverages-row-value/ac-variants-coverages-row-value.component';
import { AcVariantsCoveragesRowTitleComponent } from './sections/web/ac-variants-beesafe-section/ac-variants-coverages-table/ac-variants-coverages-row-title/ac-variants-coverages-row-title.component';
import { AcVariantsComparisonsDialogComponent } from './sections/web/ac-variants-beesafe-section/ac-variants-comparisons-dialog/ac-variants-comparisons-dialog.component';
import { BeesafeCoverageComparisonDialogComponent } from './sections/web/additional-options-beesafe-section/beesafe-coverage-comparision-dialog/beesafe-coverage-comparison-dialog.component';
import { CommaReplacementPipePipe } from './pipes/comma-replacement.pipe';
import { ContactInsurerDataSectionComponent } from './sections/web/contact-insurer-data-section/contact-insurer-data-section.component';
import { AdditionalDataSectionComponent } from './sections/web/additional-data-section/additional-data-section.component';
import { CookieService } from 'ngx-cookie-service';
import { AppCookieService } from './services/app-cookie.service';
import { CryptoService } from './services/crypto.service';
import { CalculationCacheVerificationDialogComponent } from './components/dialogs/calculation-cache-veryfication-dialog/calculation-cache-verification-dialog.component';
import { CalculationsCacheSectionComponent } from './sections/web/calculations-cache-section/calculations-cache-section.component';
import { DriverSosDetailsDialogComponent } from './components/dialogs/driver-sos-details-dialog/driver-sos-details-dialog.component';
import { PdfViewerDialogComponent } from './components/dialogs/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { ContainerSpinnerComponent } from './components/presentation/container-spinner/container-spinner.component';
import {DriverSosDetailsBannerComponent} from './components/banners/driver-sos-details-banner/driver-sos-details-banner.component';
import {DriverSosBannerComponent} from './components/banners/driver-sos-banner/driver-sos-banner.component';
import {AccidentsHappenBannerComponent} from './components/banners/accidents-happen-banner/accidents-happen-banner.component';
import {StepBannerComponent} from './components/banners/step-banner/step-banner.component';
import {BuyPolicyBannerComponent} from './components/banners/buy-policy-banner/buy-policy-banner.component';
import { DriverSosDescriptionComponent } from './components/banners/driver-sos-details-banner/driver-sos-description/driver-sos-description.component';
import { OtherProductsComponent } from './components/other-products/other-products.component';
import { ProductTileComponent } from './components/product-tile/product-tile.component';
import { HelplineWidgetComponent } from './components/presentation/helpline-widget/helpline-widget.component';
import { SimpleNotificationComponent } from './sections/notifications/simple-notification/simple-notification.component';
import { ChipsListComponent } from './components/chips-list/chips-list.component';
import { ChipValueComponent } from './components/chips-list/chip-value/chip-value.component';
import { VehicleModelListItemComponent } from './components/fields/ng-multiselect/vehicle-model-list-item/vehicle-model-list-item.component';
import { DropdownComponent } from './components/presentation/dropdown/dropdown.component';
import { OfferComponent } from './sections/web/offer-section/offer/offer.component';
import { OfferPriceComponent } from './sections/web/offer-section/offer-price/offer-price.component';
import {OfferSummaryComponent} from './components/offer-summary/offer-summary.component';
import { TextFieldEditComponent } from './components/presentation/text-field-edit/text-field-edit.component';
import { NumberStepInputComponent } from './components/fields/number-step-input/number-step-input.component';
import { Ga4EventsLogComponent } from './components/debugging/ga4-events-log/ga4-events-log.component';
import { StickyCloseDialogComponent } from './components/dialogs/sticky-close-dialog/sticky-close-dialog.component';
import { AztecScannerInfoComponent } from './sections/web/vehicle-model-section/aztec-scanner-info/aztec-scanner-info.component';
import { AztecScannerQrDialogComponent } from './components/dialogs/aztec-scanner-qr-dialog/aztec-scanner-qr-dialog.component';
import { NextSendOfferComponent } from './components/next/next-send-offer/next-send-offer.component';
import { ButtonToggleComponent } from './components/fields/button-toggle/button-toggle.component';
import { NextAztecScannerComponent } from './components/next/next-aztec-scanner/next-aztec-scanner.component';
import { NextAztecScannerIntroductionComponent } from './components/next/next-aztec-scanner/next-aztec-scanner-introduction/next-aztec-scanner-introduction.component';
import { NextAztecScannerScannerComponent } from './components/next/next-aztec-scanner/next-aztec-scanner-scanner/next-aztec-scanner-scanner.component';
import { NextAztecScannerNotificationComponent } from './components/next/next-aztec-scanner/next-aztec-scanner-notification/next-aztec-scanner-notification.component';
import { SelectDatepickerListComponent } from './components/dialogs/select-dialog/select-datepicker-list/select-datepicker-list.component';
import { SeparateInsurerDataSectionComponent } from './sections/web/separate-insurer-data-section/separate-insurer-data-section.component';
import {BootstrapDatepickerComponent} from './components/fields/datepickers/bootstrap-datepicker/bootstrap-datepicker.component';
import { MaterialDatepickerComponent } from './components/fields/datepickers/material-datepicker/material-datepicker.component';
import { MaterialYearPickerComponent } from './components/fields/datepickers/material-datepicker/material-year-picker/material-year-picker.component';
import { OfferRecommendationsSectionComponent } from './sections/web/offer-recommendations-section/offer-recommendations-section.component';
import { OfferRecommendationTileComponent } from './sections/web/offer-recommendations-section/offer-recommendation-tile/offer-recommendation-tile.component';
import { MissingRecommendationTileComponent } from './sections/web/offer-recommendations-section/missing-recommendation-tile/missing-recommendation-tile.component';
import { NextOffersSectionComponent } from './sections/web/next-offers-section/next-offers-section.component';
import { InsuranceDataSummaryComponent } from './sections/web/next-offers-section/insurance-data-summary/insurance-data-summary.component';
import { OffersControlPanelComponent } from './sections/web/next-offers-section/offers-control-panel/offers-control-panel.component';
import { NextOfferComponent } from './sections/web/next-offers-section/next-offer/next-offer.component';
import { DialogHeaderComponent } from './components/dialogs/dialog-header/dialog-header.component';
import { NextOffersPresentationDialogComponent } from './sections/web/next-offers-section/next-offers-presentation-dialog/next-offers-presentation-dialog.component';
import { NextOfferDetailBoxComponent } from './sections/web/next-offers-section/next-offer-detail-box/next-offer-detail-box.component';
import { RadioCheckboxTileComponent } from './components/fields/radio-checkbox-tile/radio-checkbox-tile.component';
import { NextPaymentMethodSectionComponent } from './sections/web/next-payment-method-section/next-payment-method-section.component';
import { OffersListComponent } from './sections/web/next-offers-section/offers-list/offers-list.component';
import { OffersTableComponent } from './sections/web/next-offers-section/offers-table/offers-table.component';
import { OffersTableCellComponent } from './sections/web/next-offers-section/offers-table/offers-table-cell/offers-table-cell.component';
import { OffersTableHeaderCellComponent } from './sections/web/next-offers-section/offers-table/offers-table-header-cell/offers-table-header-cell.component';
import { OffersTableRowComponent } from './sections/web/next-offers-section/offers-table/offers-table-row/offers-table-row.component';
import { CheckboxesGroupComponent } from './components/checkboxes-group/checkboxes-group.component';
import { NextSuccessNotificationComponent } from './sections/notifications/next-success-notification/next-success-notification.component';
import { NextOtherProductsComponent } from './components/next/next-other-products/next-other-products.component';
import { AztecProcessInfoComponent } from './sections/web/vehicle-model-section/aztec-process-info/aztec-process-info.component';
import {NgxQRCodeModule} from '@techiediaries/ngx-qrcode';
import { QrCodeComponent } from './components/presentation/qr-code/qr-code.component';
import { CompletedActionInfoComponent } from './components/presentation/completed-action-info/completed-action-info.component';
import { CustomerAgreementCaptureComponent } from './sections/web/vehicle-model-section/customer-agreement-capture/customer-agreement-capture.component';
import { ImagePreviewDialogComponent } from './components/dialogs/image-preview-dialog/image-preview-dialog.component';
import {WebcamModule} from 'ngx-webcam';
import { CameraComponent } from './components/camera/camera.component';
import {LowestFirstInstalmentPipe} from './pipes/lowest-first-instalment.pipe';
import { OffersProgressDialogComponent } from './components/dialogs/offers-progress-dialog/offers-progress-dialog.component';
import { OffersProgressDialogLogoComponent } from './components/dialogs/offers-progress-dialog/offers-progress-dialog-logo/offers-progress-dialog-logo.component';
import { ResourceUrlPipe } from './pipes/resource-url.pipe';
import { NotificationWrapperComponent } from './components/notification-wrapper/notification-wrapper.component';
import { NextNotificationComponent } from './components/notification-wrapper/next-notification/next-notification.component';
import { FeaturedProductTileComponent } from './components/presentation/featured-product-tile/featured-product-tile.component';
import { FooterComponent } from './components/presentation/footer/footer.component';
import { FeaturedProductIconGroupComponent } from './components/presentation/featured-product-icon-group/featured-product-icon-group.component';
import { FeaturedProductIconComponent } from './components/presentation/featured-product-icon/featured-product-icon.component';
import { SpinnerComponent } from './components/presentation/spinner/spinner.component';
import {NoOffersTileComponent} from './sections/web/next-offers-section/no-offers-tile/no-offers-tile.component';
import { OffersComparisonPanelComponent } from './sections/web/next-offers-section/offers-comparison-panel/offers-comparison-panel.component';
import { OffersComparisonPanelOfferComponent } from './sections/web/next-offers-section/offers-comparison-panel/offers-comparison-panel-offer/offers-comparison-panel-offer.component';
import { InitialDataSectionComponent } from './sections/web/initial-data-section/initial-data-section.component';
import { CalculationAgreementsSectionComponent } from './sections/web/calculation-agreements-section/calculation-agreements-section.component';
import { SendOfferBannerComponent } from './components/banners/send-offer-banner/send-offer-banner.component';
import { OffersComparisonDialogComponent } from './components/dialogs/offers-comparison-dialog/offers-comparison-dialog.component';
import { OffersComparisonCoveragesGroupComponent } from './components/dialogs/offers-comparison-dialog/offers-comparison-coverages-group/offers-comparison-coverages-group.component';
import {GlobalErrorHandler} from './utils/global-error-handler';
import { OfferUpdateDialogComponent } from './components/dialogs/offer-update-dialog/offer-update-dialog.component';
import { LoadingContentPlaceholderComponent } from './components/presentation/loading-content-placeholder/loading-content-placeholder.component';
import { OfferUnavailableDialogComponent } from './components/dialogs/offer-unavailable-dialog/offer-unavailable-dialog.component';
import {TranslateModule} from '@ngx-translate/core';

export interface SharedModuleConfig {
  translateService?: Provider
}

@NgModule({
  imports: [
    NgbModule,
    FormsModule,
    CommonModule,
    RouterModule,
    SwiperModule,
    TextMaskModule,
    NgSelectModule,
    HttpClientModule,
    ReactiveFormsModule,
    MaterialDesignModule,
    FlexLayoutModule,
    NgxQRCodeModule,
    WebcamModule,
    NgxMaskModule.forRoot(),
    TranslateModule,
  ],

  declarations: [
    SafeHtmlPipe,
    TranslatePipe,
    MaterialSelectComponent,
    SectionComponent,
    CheckboxComponent,
    TextFieldComponent,
    SlideToggleComponent,
    MaterialMultiselectComponent,
    TextTransformInputDirective,
    DecimalPlacesAccessorDirective,
    TrimValueAccessorDirective,
    BootstrapDatepickerComponent,
    DateFieldJQueryComponent,
    EspDateFieldComponent,
    InsurerDataSectionComponent,
    AgreementsSectionComponent,
    AutocompleteComponent,
    AmountFormatterPipe,
    NotificationComponent,
    RadioButtonComponent,
    MainPricePipe,
    SelectLabelPipe,
    TailsPricePipe,
    NotificationComponent,
    SelectedOfferSectionComponent,
    OfferSelectedItemComponent,
    PositiveNotificationWebComponent,
    ErrorNotifySectionWebComponent,
    OfferSectionComponent,
    FxIfPipe,
    ErrorNotifySectionWebComponent,
    ClickOutsideDirective,
    ChipsContainerComponent,
    MultiselectAndChipListComponent,
    SelectComponent,
    PhoneFieldComponent,
    MethodPaymentsSectionComponent,
    VehicleDataSectionComponent,
    OwnerDataSectionComponent,
    CoOwnerComponent,
    InformationSectionComponent,
    VehicleModelSectionComponent,
    InformationSectionComponent,
    OffersPresentationComponent,
    OfferPresentationCoverageItemComponent,
    OffersPresentationComponent,
    AdditionalInformationSectionComponent,
    MaterialDatepickerComponent,
    TooltipIconComponent,
    TextareaComponent,
    ShortenedVehicleDataComponent,
    TextareaComponent,
    WaitingOfferDialogComponent,
    OfferPrimaryBoxComponent,
    OfferDetailBoxComponent,
    OfferBasicBoxComponent,
    OfferPresentationGroupBoxComponent,
    LowestFirstInstalmentPipe,
    OfferCompareBoxComponent,
    OfferMainPricePipe,
    OfferTailsPricePipe,
    OfferFixedPriceComponent,
    OfferCarValuationComponent,
    OfferBasicItemComponent,
    InstallmentSectionComponent,
    RadioWithMessageComponent,
    AddressDataComponent,
    SendOfferComponent,
    InformationDialogComponent,
    AmountNounTranslationPipe,
    CollectedCarsComponent,
    SendOfferNumberComponent,
    SelectDialogComponent,
    SeparatorPipe,
    CommaReplacementPipePipe,
    InformationIconComponent,
    LoadingOffersDialogComponent,
    AdditionalInfoPipe,
    VehicleVersionComponent,
    OfferRecalculationDialogComponent,
    CustomPriceComponent,
    PaymentAcceptNotificationComponent,
    CessionSectionComponent,
    InsurerDataAuthorizationComponent,
    InsurerFailedDataAuthorizationComponent,
    AztecScannerDialogComponent,
    AdditionalContentSectionComponent,
    AztecSectionComponent,
    SectionDataPresentationComponent,
    StepInfoComponent,
    AdditionalDriverComponent,
    VehicleUseSectionComponent,
    ContactDataSectionComponent,
    OffersFiltersComponent,
    OffersFilterComponent,
    NoFilteredOffersComponent,
    MobileFiltersDialogComponent,
    InsuranceCoveragesSectionComponent,
    HintComponent,
    MissingDataOfferComponent,
    MissingDataOfferComponent,
    SalesMessagesSectionComponent,
    SalesMessageComponent,
    ShortenedVehicleInfoComponent,
    SendCalculationComponent,
    InformationClauseSectionComponent,
    DiscountCodesSectionComponent,
    CustomerDamageDataComponent,
    AcVariantsSectionComponent,
    AdditionalOptionsSectionComponent,
    RadioTileComponent,
    UniqaDiscountCodeSectionComponent,
    OfferModificationPriceComponent,
    PzuBannerComponent,
    OfferModificationDialogComponent,
    AssistanceCoveragesComparisonComponent,
    AssistanceCoverageValueComponent,
    ModifiedOfferPriceChangeDialogComponent,
    MpayAztecScannerDialogComponent,
    PersonalDataAdministratorSectionComponent,
    PowerOfAttorneySectionComponent,
    SendCalculationDialogComponent,
    AcVariantsBeesafeSectionComponent,
    AdditionalOptionsBeesafeSectionComponent,
    AcVariantsCoveragesTableComponent,
    AcVariantsCoveragesRowValueComponent,
    AcVariantsCoveragesRowTitleComponent,
    AcVariantsComparisonsDialogComponent,
    BeesafeCoverageComparisonDialogComponent,
    AdditionalDataSectionComponent,
    ContactInsurerDataSectionComponent,
    CalculationCacheVerificationDialogComponent,
    CalculationsCacheSectionComponent,
    DriverSosDetailsDialogComponent,
    PdfViewerDialogComponent,
    SafeUrlPipe,
    ContainerSpinnerComponent,
    StepBannerComponent,
    BuyPolicyBannerComponent,
    AccidentsHappenBannerComponent,
    DriverSosBannerComponent,
    DriverSosDetailsBannerComponent,
    DriverSosDescriptionComponent,
    OtherProductsComponent,
    ProductTileComponent,
    HelplineWidgetComponent,
    SimpleNotificationComponent,
    ChipsListComponent,
    ChipValueComponent,
    VehicleModelListItemComponent,
    DropdownComponent,
    OfferComponent,
    OfferSummaryComponent,
    OfferPriceComponent,
    TextFieldEditComponent,
    NumberStepInputComponent,
    Ga4EventsLogComponent,
    StickyCloseDialogComponent,
    AztecScannerInfoComponent,
    AztecScannerQrDialogComponent,
    NextSendOfferComponent,
    ButtonToggleComponent,
    NextAztecScannerComponent,
    NextAztecScannerIntroductionComponent,
    NextAztecScannerScannerComponent,
    NextAztecScannerNotificationComponent,
    SelectDatepickerListComponent,
    SeparateInsurerDataSectionComponent,
    MaterialDatepickerComponent,
    MaterialYearPickerComponent,
    OfferRecommendationsSectionComponent,
    OfferRecommendationTileComponent,
    MissingRecommendationTileComponent,
    NextOffersSectionComponent,
    InsuranceDataSummaryComponent,
    OffersControlPanelComponent,
    NextOfferComponent,
    DialogHeaderComponent,
    NextOffersPresentationDialogComponent,
    NoOffersTileComponent,
    NextOfferDetailBoxComponent,
    RadioCheckboxTileComponent,
    NextPaymentMethodSectionComponent,
    OffersListComponent,
    OffersTableComponent,
    OffersTableCellComponent,
    OffersTableHeaderCellComponent,
    OffersTableRowComponent,
    CheckboxesGroupComponent,
    NextSuccessNotificationComponent,
    NextOtherProductsComponent,
    AztecProcessInfoComponent,
    QrCodeComponent,
    CompletedActionInfoComponent,
    CustomerAgreementCaptureComponent,
    ImagePreviewDialogComponent,
    CameraComponent,
    OffersProgressDialogComponent,
    OffersProgressDialogLogoComponent,
    ResourceUrlPipe,
    NotificationWrapperComponent,
    NextNotificationComponent,
    FeaturedProductTileComponent,
    FooterComponent,
    FeaturedProductIconGroupComponent,
    FeaturedProductIconComponent,
    SpinnerComponent,
    OffersComparisonPanelComponent,
    OffersComparisonPanelOfferComponent,
    InitialDataSectionComponent,
    CalculationAgreementsSectionComponent,
    SendOfferBannerComponent,
    OffersComparisonDialogComponent,
    OffersComparisonCoveragesGroupComponent,
    OfferUpdateDialogComponent,
    LoadingContentPlaceholderComponent,
    OfferUnavailableDialogComponent,
  ],

    exports: [
        NgxMaskModule,
        ShortenedVehicleDataComponent,
        InsurerDataSectionComponent,
        AgreementsSectionComponent,
        OfferSectionComponent,
        MaterialDesignModule,
        FlexLayoutModule,
        OfferPresentationCoverageItemComponent,
        SendOfferComponent,
        CollectedCarsComponent,
        SendOfferComponent,
        SendOfferNumberComponent,
        StepInfoComponent,
        ShortenedVehicleInfoComponent,
        InformationClauseSectionComponent,
        DiscountCodesSectionComponent,
        CustomerDamageDataComponent,
        BeesafeCoverageComparisonDialogComponent,
        CalculationCacheVerificationDialogComponent,
        SafeHtmlPipe,
        SafeUrlPipe,
        StepBannerComponent,
        HelplineWidgetComponent,
        OfferSummaryComponent,
        Ga4EventsLogComponent,
        NextSendOfferComponent,
        ContainerSpinnerComponent,
        CameraComponent,
        SendOfferBannerComponent,
    ],
  providers: [
    LanguageService,
    VehicleModelStateService,
    VehicleDataStateService,
    VehicleService,
    RequestStorageService,
    CookieService,
    AppCookieService,
    CryptoService,
    SeparatorPipe,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ],
  entryComponents: [
    InsurerDataSectionComponent,
    AgreementsSectionComponent,
    SelectedOfferSectionComponent,
    PositiveNotificationWebComponent,
    ErrorNotifySectionWebComponent,
    MethodPaymentsSectionComponent,
    ErrorNotifySectionWebComponent,
    OfferSectionComponent,
    InformationSectionComponent,
    OffersPresentationComponent,
    InformationSectionComponent,
    VehicleDataSectionComponent,
    OwnerDataSectionComponent,
    VehicleModelSectionComponent,
    AdditionalInformationSectionComponent,
    WaitingOfferDialogComponent,
    LoadingOffersDialogComponent,
    InstallmentSectionComponent,
    InformationDialogComponent,
    OfferRecalculationDialogComponent,
    SelectDialogComponent,
    PaymentAcceptNotificationComponent,
    InsurerDataAuthorizationComponent,
    InsurerFailedDataAuthorizationComponent,
    CessionSectionComponent,
    AztecScannerDialogComponent,
    AdditionalContentSectionComponent,
    AztecSectionComponent,
    VehicleUseSectionComponent,
    ContactDataSectionComponent,
    MobileFiltersDialogComponent,
    InsuranceCoveragesSectionComponent,
    SalesMessagesSectionComponent,
    InformationClauseSectionComponent,
    DiscountCodesSectionComponent,
    AcVariantsSectionComponent,
    AdditionalOptionsSectionComponent,
    UniqaDiscountCodeSectionComponent,
    OfferModificationDialogComponent,
    AssistanceCoveragesComparisonComponent,
    ModifiedOfferPriceChangeDialogComponent,
    PersonalDataAdministratorSectionComponent,
    PowerOfAttorneySectionComponent,
    SendCalculationDialogComponent,
    AcVariantsBeesafeSectionComponent,
    AdditionalOptionsBeesafeSectionComponent,
    AcVariantsComparisonsDialogComponent,
    BeesafeCoverageComparisonDialogComponent,
    AdditionalDataSectionComponent,
    ContactInsurerDataSectionComponent,
    CalculationCacheVerificationDialogComponent,
    CalculationsCacheSectionComponent,
    DriverSosDetailsDialogComponent,
    PdfViewerDialogComponent,
    AztecScannerQrDialogComponent,
    OfferRecommendationsSectionComponent,
    NextOffersSectionComponent,
    NextOffersPresentationDialogComponent,
    NextPaymentMethodSectionComponent,
    ImagePreviewDialogComponent,
    OffersProgressDialogComponent,
    InitialDataSectionComponent,
    CalculationAgreementsSectionComponent,
    OffersComparisonDialogComponent,
    OfferUpdateDialogComponent,
    OfferUnavailableDialogComponent,
  ]
})
export class SharedModule {
  static forRoot(config: SharedModuleConfig = {}): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [
        RestApiService,
        DatefieldService,
        CommonApiErrorHandlerService,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: HttpInterceptor,
          multi: true
        }
      ]
    }
  }
}
